import React, {FC} from 'react';
import styled from 'styled-components';

import {buildInteractionIdProps} from '../../../../libs/shared/helpers/src/interaction/interactionHelpers';
import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {DropdownItem} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {SimpleDropdown} from '../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {PopoverCoordinator} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {PaletteColorsEnum} from '../../../../libs/shared/ui/src/styles/paletteModel';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useClipboard} from '../../../helpers/hooks/clipboardHookHelpers';
import {buildMessagePublicId} from '../../../helpers/models/messageHelpers';
import {useAppDispatch} from '../../../helpers/reduxHelpers';
import {MessageModel} from '../../../models/api/messageModel';
import {deleteMessageTask} from '../../../services/tasks/deleteMessageTask';
import {TimelineVariationsEnum} from '../../../store/timelineVariations/timelineVariationSelectors';
import {DropdownItemIcon} from '../../common/dropdowns/dropdownItemIcon';
import {MessageBase} from '../viewer/activity/message/messageBase';

/*
 * Props.
 */

interface ComposerErrorFallbackProps {
  message: MessageModel;
  timelineVariation: TimelineVariationsEnum;
}

/*
 * Style.
 */

const StyledDiv = styled.div`
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

/*
 * Component.
 */

export const ComposerErrorFallback: FC<ComposerErrorFallbackProps> = (props) => {
  const {message, timelineVariation} = props;
  const dispatch = useAppDispatch();
  const {copy} = useClipboard();
  const theme = useUserTheme();

  const onCopyMessageId = () => {
    copy(message.id ? buildMessagePublicId(message) : message.uid);
  };

  const onDeleteClick = () => {
    dispatch(deleteMessageTask(message.uid));
  };

  return (
    <MessageBase timelineVariation={timelineVariation}>
      <StyledDiv {...buildInteractionIdProps('composer-error-fallback')}>
        <Trans id="composererror-message">Something went wrong with this draft</Trans>
        <PopoverCoordinator
          renderButton={(isOpen) => (
            <IconButton
              color={theme.greys.shade80}
              hoverColor={theme.greys.shade80}
              iconName="moreVertical"
              label={<Trans id="composererror-more">More</Trans>}
              hasHover={isOpen}
            />
          )}
          renderModal={(popoverProps) => (
            <SimpleDropdown {...popoverProps} placement="bottom-end">
              <DropdownItem onSelect={onCopyMessageId}>
                <DropdownItemIcon
                  iconSize={16}
                  color={theme.greys.shade80}
                  iconName="copySmall"
                  label={null}
                />
                <DropdownItemContent>
                  <Trans id="composererror-copyid">Copy message ID</Trans>
                </DropdownItemContent>
              </DropdownItem>

              <DropdownItem onSelect={onDeleteClick}>
                <DropdownItemIcon
                  iconName="trashSmall"
                  color={theme.palette.red.shade40}
                  label={null}
                  iconSize={16}
                />
                <DropdownItemContent color={PaletteColorsEnum.RED}>
                  <Trans id="composererror-delete">Delete draft</Trans>
                </DropdownItemContent>
              </DropdownItem>
            </SimpleDropdown>
          )}
        />
      </StyledDiv>
    </MessageBase>
  );
};
